import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { createDgd } from "../api/dgd"
import { fmt } from "../helpers/format"

const formatEuros = cents =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((cents || 0) / 100)

const toCents = value => {
  if (value == null || value === "") return 0
  const normalized = String(value).replace(/\s/g, "").replace(",", ".")
  const n = parseFloat(normalized)
  if (isNaN(n)) return 0
  return Math.round(n * 100)
}

const Row = ({ label, value, tone, hint }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className={`text-[12.5px] ${tone || "text-slate-600"}`}>{label}</span>
    <span className={`text-[13.5px] tabular-nums font-medium ${tone || "text-slate-900"}`}>{value}</span>
    {hint && <span className="text-[11px] text-slate-400 ml-2">{hint}</span>}
  </div>
)

const DgdModal = ({ isOpen, projectId, preview, onClose }) => {
  const [penaltiesEuros, setPenaltiesEuros] = useState("0")
  const [saving,         setSaving]         = useState(false)
  const [errors,         setErrors]         = useState([])

  const penaltiesCents = useMemo(() => toCents(penaltiesEuros), [penaltiesEuros])

  const revisedCents     = preview?.revised_contract?.cents       || 0
  const billedCents      = preview?.billed_subtotal?.cents        || 0
  const soldeCents       = preview?.solde_ht?.cents               || 0
  const holdbackCents    = preview?.holdback_release?.cents       || 0
  const dpRemainingCents = preview?.down_payment_remaining?.cents || 0
  const totalHtCents     = soldeCents + holdbackCents - penaltiesCents - dpRemainingCents

  const handleSave = () => {
    if (saving) return
    setSaving(true)
    setErrors([])

    createDgd(projectId, { penalties_cents: penaltiesCents })
      .then(response => {
        if (response?.errors) {
          setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
          return
        }
        if (response?.redirect_to) {
          window.location.href = response.redirect_to
          return
        }
        window.location.reload()
      })
      .catch(() => setErrors([I18n.t("projects.dgds.errors.network")]))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div className="text-[12px] text-slate-500">{I18n.t("projects.dgds.form.locks_project_hint")}</div>
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("projects.dgds.form.cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          <FontAwesomeIcon icon={saving ? "spinner" : "flag-checkered"} spin={saving} />
          {I18n.t("projects.dgds.form.create")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {I18n.t("projects.dgds.form.eyebrow")}
      title   = {I18n.t("projects.dgds.form.title")}
      footer  = {footer}
    >
      <p className="text-[12.5px] text-slate-600 mb-4">{I18n.t("projects.dgds.form.intro")}</p>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
            {I18n.t("projects.dgds.form.breakdown_title")}
          </div>
        </div>

        <div className="px-4 py-2 divide-y divide-slate-100">
          <Row label={I18n.t("projects.dgds.form.revised_contract")} value={fmt(preview?.revised_contract)} />
          <Row label={I18n.t("projects.dgds.form.billed_subtotal")}  value={`− ${fmt(preview?.billed_subtotal)}`} tone="text-slate-700" />
          <Row label={I18n.t("projects.dgds.form.solde_ht")}         value={fmt(preview?.solde_ht)} tone="text-slate-900 font-semibold" />
        </div>

        <div className="px-4 py-2 divide-y divide-slate-100 border-t border-slate-200">
          <Row label={I18n.t("projects.dgds.form.holdback_release")} value={`+ ${fmt(preview?.holdback_release)}`} tone="text-emerald-700" />

          <div className="flex items-center justify-between gap-3 py-1.5">
            <label className="text-[12.5px] text-slate-600 flex items-center gap-2">
              {I18n.t("projects.dgds.form.penalties")}
              <input
                value     = {penaltiesEuros}
                onChange  = {e => setPenaltiesEuros(e.target.value)}
                className = "w-24 h-7 px-2 text-[12px] text-right border border-slate-200 rounded tabular-nums"
              />
              <span className="text-[11px] text-slate-400">{I18n.t("projects.dgds.form.currency_excl_tax")}</span>
            </label>
            <span className="text-[13.5px] tabular-nums text-rose-700 font-medium">
              {penaltiesCents > 0 ? `− ${formatEuros(penaltiesCents)}` : "—"}
            </span>
          </div>

          <Row
            label={I18n.t("projects.dgds.form.down_payment_balance")}
            value={dpRemainingCents > 0 ? `− ${fmt(preview?.down_payment_remaining)}` : "—"}
            tone={dpRemainingCents > 0 ? "text-rose-700" : "text-slate-400"}
          />
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-baseline justify-between">
          <span className="text-[13px] font-semibold text-slate-900">{I18n.t("projects.dgds.form.total_ht")}</span>
          <span className="text-[18px] font-bold tabular-nums text-slate-900">{formatEuros(totalHtCents)}</span>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}
    </Modal>
  )
}

export default DgdModal
